import {resolveTodayAssessments} from '@/ts/composables/pure/use-today-assessments';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {
  AssessmentStatusDTO,
  type AssessmentTileDTO,
  type TodayAssessmentDTO,
} from '@/ts/types/dto/today-dto';
import {
  type TodayAssessmentItem,
  TodayAssessmentStatus,
  type TodayAssessmentView,
} from '@/ts/types/component/today-page';

const STATUS: Record<AssessmentStatusDTO, TodayAssessmentStatus> = {
  [AssessmentStatusDTO.AVAILABLE]: TodayAssessmentStatus.available,
  [AssessmentStatusDTO.LOCKED]: TodayAssessmentStatus.locked,
};

/**
 * How long until it reopens, which is the one thing the payload does not state: it sends the instant
 * and the view-model wants the duration, so the subtraction happens here and nowhere else. `now`
 * arrives as an argument, so the same payload and the same clock always give the same answer.
 *
 * The duration is carried across whatever the status says. An available assessment CAN arrive with a
 * date, in the past, and dropping it is not this function's call to make — `resolveTodayAssessments`
 * reads the status first and strips the duration from every card that is not counting down. Keeping
 * that rule in one place is worth more than pre-empting it in two.
 *
 * An unparseable instant is treated as no date at all rather than travelling on as NaN, which would
 * reach the copy as a countdown of nothing.
 */
function remainingMillis(item: TodayAssessmentDTO, now: Date): number | null {
  const unlockDate = item.unlockDate ?? null;
  if (unlockDate === null) {
    return null;
  }
  const reopensAt = Date.parse(unlockDate);
  return Number.isNaN(reopensAt) ? null : reopensAt - now.getTime();
}

/**
 * The artwork is resolved to a whole URL here rather than in the card, and deliberately WITHOUT the
 * transform parameters the picture gets: those are for photographs, and asking for a drawing to be
 * re-encoded is asking to lose the animation. Directus happens to ignore them on an SVG, but relying
 * on that would be relying on a mercy.
 *
 * An absent one is left absent instead of becoming an empty string, so the card can tell "no drawing"
 * from "a drawing that failed" and answer each differently.
 */
function artworkUrl(animation: string | null | undefined): string | undefined {
  // Truthy, so an absent field and a null one are answered the same way. The type says both are
  // possible because the backend that serves these is not deployed yet.
  return animation ? calculateMediaUrl(animation) : undefined; // eslint-disable-line no-undefined
}

function toItem(item: TodayAssessmentDTO, now: Date): TodayAssessmentItem {
  const {questionnaire} = item;
  return {
    id: questionnaire.id,
    // The questionnaire's own name, which is what the current home page already shows for these same
    // cards. Nothing is derived from its topic or its type.
    title: questionnaire.name,
    status: STATUS[item.status],
    imageUrl: questionnaire.image,
    artUrl: artworkUrl(questionnaire.animation),
    // Passed through as the CMS wrote it. Whether it can be drawn is not decided here but in the
    // card, which measures it against the ink it is stuck with and keeps its own tint when the two
    // cannot be read together — and says so in the log, since a colour silently ignored would
    // otherwise leave the person who chose it with no idea why the card looks the same.
    backgroundColor: questionnaire.backgroundColor ?? undefined, // eslint-disable-line no-undefined
    unlockInMillis: remainingMillis(item, now),
  };
}

/**
 * Translates the assessment tile of `GET /me/today` into what the slider renders (IPD-3052).
 *
 * It does two things and delegates the third: it flattens each item's questionnaire into a card's
 * worth of fields, it turns the reopening instant into a remaining duration, and it hands the result
 * to {@link resolveTodayAssessments}, which owns the correspondence between the shape of the list and
 * the states the design draws. That derivation is not repeated here — it is tested where it lives,
 * and two copies of it would be free to disagree.
 *
 * The list is passed on whole, in the order it arrives. The backend decides which single assessment
 * is open and puts it first; the order of the rest is the recommendation engine's, and the engine
 * stops at ten, so the carousel never grows unbounded — implicitly, not by contract, which is why
 * nothing here relies on the number.
 *
 * NOT trimmed to the open card, deliberately. When the onboarding assessment is the open one the
 * payload still ships the whole pool behind it, and the prototype draws that state twice: once as a
 * single card and once as a carousel. Design owns which it is, and the backend will add no flag for
 * it, so if the single card wins, the trim belongs here — one line, filtering to the open item when
 * the face is `available` — and nowhere else. Until then the tile renders what it is sent.
 *
 * @param now the instant to measure the countdowns against. A duration computed here is a snapshot:
 *   it ages if the page is left open, which is harmless while the copy speaks in days and worth
 *   revisiting the day it speaks in minutes.
 * @returns null when the payload carries no tile, which is also what an empty list resolves to. The
 *   journal expands into the row on its own — the page builds no cell for a tile it is not given.
 */
export function mapTodayAssessment(
  dto: AssessmentTileDTO | null,
  now: Date,
): TodayAssessmentView | null {
  if (dto === null) {
    return null;
  }

  return resolveTodayAssessments(dto.items.map((item) => toItem(item, now)));
}
